import { useMutation, UseMutationOptions, useQueryClient } from 'react-query';

import { patchJson } from '@zapier/toolbox-browser-fetch';

import { ZAPIER_APP_BASE_URL } from 'app/common/constants';
import { useNotify } from 'app/developer-v3/hooks';

import { getQueryKey, IntegrationToken } from './useQueryIntegrationToken';

const MUTATION_KEY = 'mutate-regenerate-integration-token';

type Args = {
  integrationId: number;
};

type RegenerateResponse = {
  key: string;
  user: number;
  user_email?: string;
  created?: string;
};

function useMutationRegenerateIntegrationToken(
  args: Args,
  options?: UseMutationOptions<IntegrationToken, Error, void, unknown>
) {
  const { integrationId } = args;
  const notify = useNotify();
  const queryClient = useQueryClient();

  const queryKey = getQueryKey({ integrationId });
  const mutationKey = MUTATION_KEY;
  const email = `app_${integrationId}@zapierpartner.com`;

  const mutation = useMutation({
    mutationKey,
    async mutationFn() {
      const tokenUrl = new URL(
        `/api/platform/cli/apps/${encodeURIComponent(
          integrationId
        )}/tokens/${encodeURIComponent(email)}/`,
        ZAPIER_APP_BASE_URL
      );

      const response = (await patchJson(tokenUrl.href)) as RegenerateResponse;

      return {
        token: response.key,
        userId: response.user,
      };
    },
    onError: (error: any) => {
      notify.failure(
        error.message || 'Failed to regenerate token',
        mutationKey
      );
    },
    onSuccess: data => {
      // Update the cache with new token
      queryClient.setQueryData(queryKey, data);
      notify.success('Token regenerated successfully');
    },
    ...options,
  });

  return { ...mutation, mutationKey };
}

export default useMutationRegenerateIntegrationToken;
